const toPriceNumber = (value) => (
  typeof value === 'number' || typeof value === 'string' ? Number(value) : NaN
);

const getValidDiscountPrices = (price) => {
  const { current, amount, original } = price || {};
  const currentPrice = current ?? amount;
  const parsedCurrent = toPriceNumber(currentPrice);
  const parsedOriginal = toPriceNumber(original);

  return Number.isFinite(parsedCurrent)
    && parsedCurrent > 0
    && Number.isFinite(parsedOriginal)
    && parsedOriginal > parsedCurrent
    ? { current: parsedCurrent, original: parsedOriginal }
    : null;
};

const isValidSuppliedPercentage = (value) => (
  typeof value === 'number'
  && Number.isFinite(value)
  && value !== 0
  && Math.abs(value) <= 100
);

export const resolveDiscountPercentage = (options) => {
  const { price, settings, warn = console.warn } = options || {};
  if (!settings?.enabled) return null;

  const validPrices = getValidDiscountPrices(price);
  if (!validPrices) return null;

  const suppliedValue = price.discountPercentage;
  if (suppliedValue !== undefined && suppliedValue !== null) {
    if (isValidSuppliedPercentage(suppliedValue)) return Math.abs(suppliedValue);
    if (typeof warn === 'function') {
      warn('Invalid discountPercentage. Expected a finite, nonzero number from -100 to 100.');
    }
  }

  if (!settings.calculateWhenMissing) return null;

  const calculatedValue = Math.min(99, Math.floor(
    ((validPrices.original - validPrices.current) / validPrices.original) * 100,
  ));
  if (calculatedValue === 0) return null;

  return calculatedValue;
};

export const formatDiscountPercentage = (options) => {
  const {
    value,
    format = 'positive',
    locale,
    withLabelTemplate = '{percentage} off',
  } = options || {};
  if (!Number.isFinite(value) || value === 0) return '';

  const percentage = Math.abs(value);
  const formatValue = (signedValue) => new Intl.NumberFormat(locale, {
    style: 'percent',
    maximumFractionDigits: 20,
  }).format(signedValue / 100);
  const positivePercentage = formatValue(percentage);
  const formatters = {
    negative: () => formatValue(-percentage),
    positive: () => positivePercentage,
    positiveWithLabel: () => withLabelTemplate.replace('{percentage}', positivePercentage),
  };

  return (formatters[format] || formatters.positive)();
};

export const formatDiscountPercentageAriaLabel = (options) => {
  const { value, label, locale } = options || {};
  const prefix = typeof label === 'string' && label ? label : 'Discount';
  const percentage = formatDiscountPercentage({ value, locale });

  return percentage ? `${prefix} ${percentage}` : '';
};
